import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useVendorBids, RFQWithBid } from '@/hooks/useVendorBids';
import { JobCategory } from '@/lib/types/job';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Calendar, Building2, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function VendorBidsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<JobCategory | undefined>();
  
  const { bids, loading, error } = useVendorBids({
    search: searchQuery,
    category: selectedCategory
  });

  const formatCurrency = (cents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  };

  const getTimeRemaining = (closesAt: string) => {
    const now = new Date();
    const closeDate = new Date(closesAt);
    
    if (closeDate < now) {
      return 'Closed';
    }
    
    return `Closes in ${formatDistanceToNow(closeDate)}`;
  };

  const getBidStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      submitted: { label: 'Submitted', variant: 'default' },
      withdrawn: { label: 'Withdrawn', variant: 'secondary' },
      winner: { label: 'Winner', variant: 'default' },
      loser: { label: 'Not Selected', variant: 'secondary' }
    };

    const config = statusConfig[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getRFQStatusBadge = (rfqStatus: string) => {
    if (rfqStatus === 'open') {
      return <Badge variant="default">Open</Badge>;
    }
    return <Badge variant="secondary">Closed</Badge>;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">My Bids</h1>
        <p className="text-muted-foreground">
          Track all your submitted bids and their status
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by job title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={selectedCategory || 'all'}
          onValueChange={(value) => setSelectedCategory(value === 'all' ? undefined : value as JobCategory)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="print_materials">Print Materials</SelectItem>
            <SelectItem value="promotional_products">Promotional Products</SelectItem>
            <SelectItem value="signage">Signage</SelectItem>
            <SelectItem value="apparel">Apparel</SelectItem>
            <SelectItem value="packaging">Packaging</SelectItem>
            <SelectItem value="digital_services">Digital Services</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading bids: {error}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !error && bids.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No bids found</h3>
              <p className="text-muted-foreground mt-2">
                {searchQuery || selectedCategory
                  ? 'Try adjusting your filters'
                  : 'You haven\'t submitted any bids yet'}
              </p>
              {!searchQuery && !selectedCategory && (
                <Button asChild className="mt-4">
                  <Link to="/rfqs">Browse RFQs</Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bids Grid */}
      {!loading && !error && bids.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {bids.map((bidRfq: RFQWithBid) => (
            <Card key={bidRfq.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <CardTitle className="text-lg line-clamp-2">
                      {bidRfq.job.title}
                    </CardTitle>
                    {bidRfq.job.organization?.name && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {bidRfq.job.organization.name}
                      </p>
                    )}
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Building2 className="h-3 w-3" />
                      {bidRfq.job.category?.replace(/_/g, ' ')}
                    </CardDescription>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {getRFQStatusBadge(bidRfq.status)}
                    {getBidStatusBadge(bidRfq.bid.status)}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Bid Details */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Your Bid:</span>
                    <span className="font-semibold text-lg">
                      {formatCurrency(bidRfq.bid.price_cents, bidRfq.bid.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Lead Time:</span>
                    <span className="font-medium">{bidRfq.bid.lead_time_days} days</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Submitted:</span>
                    <span className="font-medium">
                      {formatDistanceToNow(new Date(bidRfq.bid.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>

                {/* Job Details */}
                <div className="pt-2 border-t space-y-2">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {bidRfq.job.description}
                  </p>
                  
                  {bidRfq.closes_at && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {getTimeRemaining(bidRfq.closes_at)}
                    </div>
                  )}

                  {bidRfq.bids && bidRfq.bids.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {bidRfq.bids.length} {bidRfq.bids.length === 1 ? 'bid' : 'bids'} submitted
                    </div>
                  )}
                </div>

                {/* Actions */}
                <Button asChild className="w-full">
                  <Link to={`/rfqs/${bidRfq.public_id}`}>
                    View RFQ Details
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
